import {
  FITNESS_SCHEDULE_KEYS,
  type FitnessPlanDetail,
  type FitnessPlanExercise,
  type FitnessPlanSessionSummary,
  type FitnessPlanSession,
  type FitnessPlanSessionExercise,
  type FitnessPlanSessionSet,
  type FitnessPlanSet,
  type FitnessPlanWorkout,
  type FitnessScheduleKey,
  type PlanSavePayload,
} from '../../core/api/callable-api.service';
import {
  clonePrograms,
  cloneSplit,
  emptySplit,
  legacyProgramIdForPlan,
  planDetailFromStored,
  type FitnessPlanProgram,
  type FitnessPlanSplit,
} from './fitness-plan.domain';
import { formatAppDateTime } from '../../shared/date-time/app-date-time';

export {
  clonePrograms,
  cloneSplit,
  emptySplit,
  legacyProgramIdForPlan,
  planDetailFromStored,
};
export type { FitnessPlanProgram, FitnessPlanSplit, PlanSavePayload };

export const DEFAULT_SET_REST_SEC = 90;

export function emptyProgram(
  name: string,
  order = 0,
  description = '',
): FitnessPlanProgram {
  const program: FitnessPlanProgram = {
    programId: crypto.randomUUID(),
    name: name.trim(),
    order,
    workouts: [],
    split: emptySplit(),
  };
  const desc = description.trim();
  if (desc) {
    program.description = desc;
  }
  return program;
}

export function sortedPrograms(
  programs: FitnessPlanProgram[],
): FitnessPlanProgram[] {
  return [...programs].sort((a, b) => {
    const oa =
      typeof a.order === 'number' && !Number.isNaN(a.order) ? a.order : 0;
    const ob =
      typeof b.order === 'number' && !Number.isNaN(b.order) ? b.order : 0;
    if (oa !== ob) {
      return oa - ob;
    }
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

export function programById(
  plan: FitnessPlanDetail | null | undefined,
  programId: string,
): FitnessPlanProgram | null {
  if (!plan || !programId.trim()) {
    return null;
  }
  const id = programId.trim();
  return (plan.programs ?? []).find((p) => p.programId === id) ?? null;
}

export function activeProgram(
  plan: FitnessPlanDetail | null | undefined,
): FitnessPlanProgram | null {
  if (!plan) {
    return null;
  }
  const programs = sortedPrograms(plan.programs ?? []);
  if (programs.length === 0) {
    return null;
  }
  const activeId = (plan.activeProgramId ?? '').trim();
  if (activeId) {
    const hit = programs.find((p) => p.programId === activeId);
    if (hit) {
      return hit;
    }
  }
  return programs[0] ?? null;
}

export function patchProgramInPlan(
  plan: FitnessPlanDetail,
  programId: string,
  patch: Partial<
    Pick<
      FitnessPlanProgram,
      'name' | 'description' | 'order' | 'workouts' | 'split'
    >
  >,
): FitnessPlanProgram[] {
  const id = programId.trim();
  return (plan.programs ?? []).map((p) =>
    p.programId === id ? { ...p, ...patch } : p,
  );
}

export function planSavePayload(
  plan: FitnessPlanDetail,
  overrides?: {
    programs?: FitnessPlanProgram[];
    activeProgramId?: string | null;
    name?: string;
    goal?: FitnessPlanDetail['goal'];
    notes?: string;
  },
): PlanSavePayload {
  return {
    planId: plan.planId,
    name: overrides?.name ?? plan.name,
    goal: overrides?.goal ?? plan.goal ?? undefined,
    notes: overrides?.notes ?? plan.notes ?? undefined,
    programs: overrides?.programs ?? clonePrograms(plan.programs),
    activeProgramId:
      overrides?.activeProgramId !== undefined
        ? overrides.activeProgramId
        : plan.activeProgramId,
  };
}

export function emptySet(): FitnessPlanSet {
  return {
    repRangeMin: 8,
    repRangeMax: 8,
    targetLoadKg: 0,
    restSec: DEFAULT_SET_REST_SEC,
  };
}

export function freshWorkout(): FitnessPlanWorkout {
  return {
    workoutId: crypto.randomUUID(),
    name: '',
    order: 0,
    exercises: [
      {
        exerciseId: crypto.randomUUID(),
        exerciseName: '',
        order: 0,
        sets: [emptySet()],
      },
    ],
  };
}

export function workoutDisplayName(w: FitnessPlanWorkout): string {
  const n = (w.name ?? '').trim();
  if (n) {
    return n;
  }
  const t = (w.title ?? '').trim();
  return t || 'Workout';
}

export function exerciseDisplayName(ex: FitnessPlanExercise): string {
  const n = (ex.exerciseName ?? '').trim();
  if (n) {
    return n;
  }
  return (ex.name ?? '').trim();
}

export const DAY_LABELS: Record<FitnessScheduleKey, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

export const DAY_SHORT: Record<FitnessScheduleKey, string> = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

/** True when the plan has at least one saved workout. */
export function planHasWorkouts(
  workouts: FitnessPlanWorkout[] | undefined | null,
): boolean {
  return Array.isArray(workouts) && workouts.length > 0;
}

/** True when any day is assigned to a workout (split). */
export function planHasSplitAssignments(
  split: FitnessPlanSplit | undefined | null,
): boolean {
  if (!split) {
    return false;
  }
  for (const k of FITNESS_SCHEDULE_KEYS) {
    const v = split[k];
    if (typeof v === 'string' && v.trim().length > 0) {
      return true;
    }
  }
  return false;
}

export function planHasProgramContent(program: FitnessPlanProgram): boolean {
  return (
    planHasWorkouts(program.workouts) ||
    planHasSplitAssignments(program.split)
  );
}

/** Goals must not be edited once any program has workouts or split assignments. */
export function planGoalsLockedFromContent(
  programs: FitnessPlanProgram[] | undefined | null,
): boolean {
  if (!Array.isArray(programs)) {
    return false;
  }
  return programs.some((p) => planHasProgramContent(p));
}

/** One-line split summary for overview (Mon Push · Wed Pull). */
export function applyProgramFormResult(
  program: FitnessPlanProgram,
  result: { name: string; description: string },
): FitnessPlanProgram {
  const next: FitnessPlanProgram = {
    ...program,
    name: result.name.trim(),
  };
  const desc = result.description.trim();
  if (desc) {
    next.description = desc;
  } else {
    delete next.description;
  }
  return next;
}

export function splitSummaryLine(
  split: FitnessPlanSplit | undefined | null,
  workouts: FitnessPlanWorkout[] | undefined | null,
): string {
  if (!split) {
    return '';
  }
  const parts: string[] = [];
  for (const k of FITNESS_SCHEDULE_KEYS) {
    const id = split[k];
    if (!id) {
      continue;
    }
    const w = (workouts ?? []).find((x) => x.workoutId === id);
    const label = w ? workoutDisplayName(w) : id;
    parts.push(`${DAY_SHORT[k]} ${label}`);
  }
  return parts.join(' · ');
}

/** Workout ids assigned on Split (Sun→Sat), deduped, first occurrence wins. */
export function splitWorkoutRotation(program: FitnessPlanProgram): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const k of FITNESS_SCHEDULE_KEYS) {
    const id = program.split?.[k];
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function nextWorkoutIdInRotation(
  rotation: readonly string[],
  lastCompletedWorkoutId: string | null | undefined,
): string | null {
  if (rotation.length === 0) {
    return null;
  }
  if (!lastCompletedWorkoutId?.trim()) {
    return rotation[0];
  }
  const last = lastCompletedWorkoutId.trim();
  const idx = rotation.indexOf(last);
  if (idx < 0) {
    return rotation[0];
  }
  return rotation[(idx + 1) % rotation.length];
}

export function lastCompletedSessionForProgram(
  sessions: readonly FitnessPlanSessionSummary[],
  programId: string,
): FitnessPlanSessionSummary | null {
  const completed = sessions.filter(
    (s) => s.programId === programId && s.status === 'completed',
  );
  if (completed.length === 0) {
    return null;
  }
  return [...completed].sort((a, b) => {
    const at = a.finishedAt ?? a.startedAt;
    const bt = b.finishedAt ?? b.startedAt;
    return bt.localeCompare(at);
  })[0];
}

export function inProgressSessionForProgram(
  sessions: readonly FitnessPlanSessionSummary[],
  programId: string,
): FitnessPlanSessionSummary | null {
  return (
    sessions.find(
      (s) => s.programId === programId && s.status === 'in_progress',
    ) ?? null
  );
}

export function formatSessionWhen(iso: string | null | undefined): string {
  return formatAppDateTime(iso, 'full');
}

/** Time-only line for session history rows (date is in the group heading). */
export function sessionHistorySubtitle(session: {
  startedAt?: string | null;
  finishedAt?: string | null;
}): string {
  return formatAppDateTime(session.finishedAt ?? session.startedAt, 'time');
}

export function sessionElapsedMs(
  startedAt: string | null | undefined,
  finishedAt?: string | null,
): number {
  const start = (startedAt ?? '').trim();
  if (!start) {
    return 0;
  }
  const startMs = new Date(start).getTime();
  if (Number.isNaN(startMs)) {
    return 0;
  }
  const endRaw = (finishedAt ?? '').trim();
  const endMs = endRaw ? new Date(endRaw).getTime() : Date.now();
  if (Number.isNaN(endMs)) {
    return Math.max(0, Date.now() - startMs);
  }
  return Math.max(0, endMs - startMs);
}

export function formatSessionDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function formatSessionDateHeading(iso: string | null | undefined): string {
  return formatAppDateTime(iso, 'date');
}

export interface SessionDateGroup {
  dateKey: string;
  dateLabel: string;
  sessions: FitnessPlanSessionSummary[];
}

export function groupSessionsByDate(
  sessions: readonly FitnessPlanSessionSummary[],
): SessionDateGroup[] {
  const map = new Map<string, FitnessPlanSessionSummary[]>();
  for (const s of sessions) {
    const raw = s.finishedAt ?? s.startedAt;
    const d = new Date(raw);
    const key = Number.isNaN(d.getTime())
      ? raw
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const list = map.get(key) ?? [];
    list.push(s);
    map.set(key, list);
  }
  return [...map.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dateKey, rows]) => ({
      dateKey,
      dateLabel: formatSessionDateHeading(rows[0]?.finishedAt ?? rows[0]?.startedAt),
      sessions: rows,
    }));
}

export function findPreviousCompletedSession(
  sessions: readonly FitnessPlanSessionSummary[],
  programId: string,
  workoutId: string,
  excludeSessionId?: string,
): FitnessPlanSessionSummary | null {
  const completed = sessions.filter(
    (s) =>
      s.programId === programId &&
      s.workoutId === workoutId &&
      s.status === 'completed' &&
      s.sessionId !== excludeSessionId,
  );
  if (completed.length === 0) {
    return null;
  }
  return [...completed].sort((a, b) => {
    const at = a.finishedAt ?? a.startedAt;
    const bt = b.finishedAt ?? b.startedAt;
    return bt.localeCompare(at);
  })[0];
}

export function lastSessionSetsForExercise(
  session: FitnessPlanSession | null,
  exerciseId: string,
): FitnessPlanSessionSet[] {
  if (!session) {
    return [];
  }
  const ex = (session.exercises ?? []).find((e) => e.exerciseId === exerciseId);
  return ex?.sets ?? [];
}

export function isSessionSetLogged(set: FitnessPlanSessionSet): boolean {
  return set.logged === true;
}

export function isSessionSetSkipped(set: FitnessPlanSessionSet): boolean {
  return set.skipped === true && set.logged !== true;
}

export function isSessionSetComplete(set: FitnessPlanSessionSet): boolean {
  return isSessionSetLogged(set) || isSessionSetSkipped(set);
}

export function findResumeSetIndex(
  exercises: readonly FitnessPlanSessionExercise[],
): { exerciseIndex: number; setIndex: number } | null {
  for (let ei = 0; ei < exercises.length; ei++) {
    const sets = exercises[ei].sets ?? [];
    for (let si = 0; si < sets.length; si++) {
      if (!isSessionSetComplete(sets[si])) {
        return { exerciseIndex: ei, setIndex: si };
      }
    }
  }
  return null;
}

/** Next incomplete set after a position (same exercise, then later exercises). */
export function findNextForwardSetIndex(
  exercises: readonly FitnessPlanSessionExercise[],
  afterExerciseIndex: number,
  afterSetIndex: number,
): { exerciseIndex: number; setIndex: number } | null {
  const currentSets = exercises[afterExerciseIndex]?.sets ?? [];
  for (let si = afterSetIndex + 1; si < currentSets.length; si++) {
    if (!isSessionSetComplete(currentSets[si])) {
      return { exerciseIndex: afterExerciseIndex, setIndex: si };
    }
  }
  for (let ei = afterExerciseIndex + 1; ei < exercises.length; ei++) {
    const sets = exercises[ei].sets ?? [];
    for (let si = 0; si < sets.length; si++) {
      if (!isSessionSetComplete(sets[si])) {
        return { exerciseIndex: ei, setIndex: si };
      }
    }
  }
  return null;
}

export function isSessionFullyLogged(
  exercises: readonly FitnessPlanSessionExercise[],
): boolean {
  return findResumeSetIndex(exercises) === null;
}

export function formatLastSessionSetLine(set: FitnessPlanSessionSet): string {
  if (isSessionSetSkipped(set)) {
    return 'Skipped';
  }
  return formatLoggedRepsAtWeight(set.reps, set.weightKg);
}

export const SESSION_DEFAULT_REST_SEC = 90;

export function effectiveSessionRestSec(
  restSec: number | null | undefined,
): number {
  return restSec != null && restSec > 0 ? restSec : SESSION_DEFAULT_REST_SEC;
}

export function sessionSetActualRestSec(set: FitnessPlanSessionSet): number {
  if (set.loggedRestSec != null && set.loggedRestSec > 0) {
    return set.loggedRestSec;
  }
  return effectiveSessionRestSec(set.restSec);
}

/** Compact weight token, e.g. `50kg`. */
export function formatSetWeightKg(weightKg: number): string {
  return `${weightKg}kg`;
}

/** Logged performance: `5 @ 50kg`, `5`, or `50kg`. */
export function formatLoggedRepsAtWeight(
  reps: number | null,
  weightKg: number | null,
): string {
  if (reps == null && weightKg == null) {
    return '—';
  }
  if (reps != null && weightKg != null) {
    return `${reps} @ ${formatSetWeightKg(weightKg)}`;
  }
  if (reps != null) {
    return `${reps}`;
  }
  return formatSetWeightKg(weightKg!);
}

/** Template rep range: `8`, `8–12`, `8+`, or `≤12`. */
export function formatTemplateRepRange(
  repMin: number | null,
  repMax: number | null,
): string {
  if (repMin != null && repMax != null) {
    return repMin === repMax ? `${repMin}` : `${repMin}–${repMax}`;
  }
  if (repMin != null) {
    return `${repMin}+`;
  }
  if (repMax != null) {
    return `≤${repMax}`;
  }
  return '';
}

/** Logged set line, e.g. `5 @ 50kg, 60s`. */
export function formatLoggedSetLine(
  reps: number | null,
  weightKg: number | null,
  restSec?: number | null,
): string {
  const base = formatLoggedRepsAtWeight(reps, weightKg);
  if (base === '—') {
    return base;
  }
  if (restSec == null || restSec < 0) {
    return base;
  }
  return `${base}, ${formatTemplateRestSec(restSec)}`;
}

/** Prescribed rest between sets, e.g. `90s rest`. */
export function formatTemplateRestSec(restSec: number | null | undefined): string {
  if (restSec == null || restSec < 0) {
    return '';
  }
  return `${restSec}s rest`;
}

/** Prescribed set line, e.g. `8–12, 90s rest`. */
export function formatTemplateSetLine(
  repMin: number | null,
  repMax: number | null,
  restSec?: number | null,
): string {
  const reps = formatTemplateRepRange(repMin, repMax);
  const rest = formatTemplateRestSec(restSec);
  if (!reps) {
    return rest || '—';
  }
  if (!rest) {
    return reps;
  }
  return `${reps}, ${rest}`;
}

/** @deprecated Use {@link formatLoggedRepsAtWeight}. */
export function formatSessionSetWeightReps(
  weightKg: number | null,
  reps: number | null,
): string {
  return formatLoggedRepsAtWeight(reps, weightKg);
}

/** Logged-set line for session summary (reps @ weight, actual rest). */
export function formatSessionSetSummary(set: FitnessPlanSessionSet): string {
  if (isSessionSetSkipped(set)) {
    return 'Skipped';
  }
  return formatLoggedSetLine(
    set.reps,
    set.weightKg,
    isSessionSetLogged(set) ? sessionSetActualRestSec(set) : null,
  );
}

export function countLoggedSessionSets(
  exercises: readonly FitnessPlanSessionExercise[],
): number {
  let n = 0;
  for (const ex of exercises) {
    for (const set of ex.sets ?? []) {
      if (isSessionSetLogged(set)) {
        n++;
      }
    }
  }
  return n;
}

export function countCompleteSessionSets(
  exercises: readonly FitnessPlanSessionExercise[],
): number {
  let n = 0;
  for (const ex of exercises) {
    for (const set of ex.sets ?? []) {
      if (isSessionSetComplete(set)) {
        n++;
      }
    }
  }
  return n;
}

function isSessionPerformanceSet(set: FitnessPlanSessionSet): boolean {
  return isSessionSetLogged(set) && !isSessionSetSkipped(set);
}

/** Positive when ``a`` is a stronger set than ``b`` (heavier, then more reps). */
function compareSessionSetsByWeightThenReps(
  a: FitnessPlanSessionSet,
  b: FitnessPlanSessionSet,
): number {
  const weightA = a.weightKg ?? Number.NEGATIVE_INFINITY;
  const weightB = b.weightKg ?? Number.NEGATIVE_INFINITY;
  if (weightA !== weightB) {
    return weightA - weightB;
  }
  return (a.reps ?? Number.NEGATIVE_INFINITY) - (b.reps ?? Number.NEGATIVE_INFINITY);
}

/** Positive when ``a`` is a stronger set than ``b`` (more reps, then heavier). */
function compareSessionSetsByRepsThenWeight(
  a: FitnessPlanSessionSet,
  b: FitnessPlanSessionSet,
): number {
  const repsA = a.reps ?? Number.NEGATIVE_INFINITY;
  const repsB = b.reps ?? Number.NEGATIVE_INFINITY;
  if (repsA !== repsB) {
    return repsA - repsB;
  }
  return (a.weightKg ?? Number.NEGATIVE_INFINITY) - (b.weightKg ?? Number.NEGATIVE_INFINITY);
}

function sessionExerciseBestWeightSet(
  exercise: FitnessPlanSessionExercise,
): FitnessPlanSessionSet | null {
  let best: FitnessPlanSessionSet | null = null;
  for (const set of exercise.sets ?? []) {
    if (!isSessionPerformanceSet(set) || set.weightKg == null) {
      continue;
    }
    if (
      best == null ||
      compareSessionSetsByWeightThenReps(set, best) > 0
    ) {
      best = set;
    }
  }
  return best;
}

function sessionExerciseBestRepsSet(
  exercise: FitnessPlanSessionExercise,
): FitnessPlanSessionSet | null {
  let best: FitnessPlanSessionSet | null = null;
  for (const set of exercise.sets ?? []) {
    if (!isSessionPerformanceSet(set) || set.reps == null) {
      continue;
    }
    if (best == null || compareSessionSetsByRepsThenWeight(set, best) > 0) {
      best = set;
    }
  }
  return best;
}

/** Best-reps line is redundant when the heaviest set already hit that rep count. */
function shouldShowSeparateBestReps(
  bestWeightSet: FitnessPlanSessionSet | null,
  bestRepsSet: FitnessPlanSessionSet | null,
): boolean {
  if (!bestRepsSet || bestRepsSet.reps == null) {
    return false;
  }
  if (!bestWeightSet || bestWeightSet.setKey === bestRepsSet.setKey) {
    return false;
  }
  const bestReps = bestRepsSet.reps;
  const weightSetReps = bestWeightSet.reps;
  if (weightSetReps == null || bestReps > weightSetReps) {
    return true;
  }
  if (bestReps < weightSetReps) {
    return false;
  }
  const weightKg = bestWeightSet.weightKg ?? Number.NEGATIVE_INFINITY;
  const repsAtKg = bestRepsSet.weightKg ?? Number.NEGATIVE_INFINITY;
  return repsAtKg > weightKg;
}

/** Heaviest logged weight for one exercise in a session. */
export function sessionExerciseBestWeightKg(
  exercise: FitnessPlanSessionExercise,
): number | null {
  return sessionExerciseBestWeightSet(exercise)?.weightKg ?? null;
}

/** Most reps in a single logged set for one exercise in a session. */
export function sessionExerciseBestReps(
  exercise: FitnessPlanSessionExercise,
): number | null {
  return sessionExerciseBestRepsSet(exercise)?.reps ?? null;
}

/** Compact best-performance line for session summary headers. */
export function formatSessionExerciseBestLine(
  exercise: FitnessPlanSessionExercise,
): string | null {
  const bestWeightSet = sessionExerciseBestWeightSet(exercise);
  const bestRepsSet = sessionExerciseBestRepsSet(exercise);
  if (!bestWeightSet && !bestRepsSet) {
    return null;
  }

  if (
    bestWeightSet &&
    bestRepsSet &&
    bestWeightSet.setKey === bestRepsSet.setKey
  ) {
    const weight = bestWeightSet.weightKg;
    const reps = bestRepsSet.reps;
    if (weight != null && reps != null) {
      return `Best weight ${formatSetWeightKg(weight)} (${reps} reps)`;
    }
    if (weight != null) {
      return `Best weight ${formatSetWeightKg(weight)}`;
    }
    if (reps != null) {
      return `Best reps ${reps}`;
    }
    return null;
  }

  const parts: string[] = [];
  if (bestWeightSet?.weightKg != null) {
    let part = `Best weight ${formatSetWeightKg(bestWeightSet.weightKg)}`;
    if (bestWeightSet.reps != null) {
      part += ` (${bestWeightSet.reps} reps)`;
    }
    parts.push(part);
  }
  if (bestRepsSet && shouldShowSeparateBestReps(bestWeightSet, bestRepsSet)) {
    let part = `Best reps ${bestRepsSet.reps}`;
    if (bestRepsSet.weightKg != null) {
      part += ` (${formatSetWeightKg(bestRepsSet.weightKg)})`;
    }
    parts.push(part);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
